import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CanEdit } from "@/shared/readOnlyManager";
import axios from "axios";
import { Table } from "reactstrap";
import { useTable } from "react-table";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";

import { getLeaderboards } from "@/services/program/getLeaderboards";
import { getLeaderboard } from "@/services/program/getLeaderboard";
import { useDispatch, flashError, flashSuccess } from "@/shared/components/flash";
import ModalWrapper from "./ModalWrapper";

const getLeaderboardTypeLabel = (typeName) => {
  switch (typeName) {
    case "Event Volume":
      return "# of Awards Received";
    case "Event Summary":
      return "Total Points Awarded";
    default:
      return "Goal Progress";
  }
};

const Leaderboards = ({
  program,
  organization,
  leaderboard,
  setLeaderboard,
  reload = 0,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [trigger, setTrigger] = useState(0);
  const [leaderboards, setLeaderboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setOpen] = useState(false);
  const [modalName, setModalName] = useState(null);
  const [busyLeaderboardId, setBusyLeaderboardId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const loadLeaderboards = useCallback(async () => {
    if (!organization?.id || !program?.id) {
      setLeaderboards([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const items = await getLeaderboards(organization.id, program.id);
      setLeaderboards(Array.isArray(items) ? items : []);
    } catch (error) {
      setLeaderboards([]);
      setErrorMessage("Unable to load leaderboards for this program.");
    } finally {
      setLoading(false);
    }
  }, [organization?.id, program?.id]);

  useEffect(() => {
    loadLeaderboards();
  }, [loadLeaderboards, reload, trigger]);

  useEffect(() => {
    const timeoutId = setTimeout(() => ReactTooltip.rebuild(), 0);
    return () => clearTimeout(timeoutId);
  }, [leaderboards, loading]);

  const toggle = useCallback((name = null, shouldReload = false) => {
    if (name) {
      setModalName(name);
      setOpen(true);
      return;
    }

    if (shouldReload) {
      setTrigger((value) => value + 1);
    }

    setOpen(false);
  }, []);

  const onClickEditLeaderboard = useCallback(
    async (leaderboardId) => {
      try {
        setBusyLeaderboardId(leaderboardId);
        const item = await getLeaderboard(organization.id, program.id, leaderboardId);
        setLeaderboard(item);
        toggle("EditLeaderboard");
      } catch (error) {
        flashError(dispatch, error?.response?.data || "Unable to load leaderboard.");
      } finally {
        setBusyLeaderboardId(null);
      }
    },
    [dispatch, organization?.id, program?.id, setLeaderboard, toggle]
  );

  const onDeleteLeaderboard = useCallback(
    async (leaderboardId) => {
      try {
        setBusyLeaderboardId(leaderboardId);
        const response = await axios.delete(
          `/organization/${organization.id}/program/${program.id}/leaderboard/${leaderboardId}`
        );

        if (response.status === 200) {
          flashSuccess(dispatch, "Leaderboard deleted successfully!");
          setLeaderboards((currentLeaderboards) =>
            currentLeaderboards.filter(
              (item) => String(item.id) !== String(leaderboardId)
            )
          );
        }
      } catch (error) {
        flashError(dispatch, error?.response?.data || "Unable to delete leaderboard.");
      } finally {
        setBusyLeaderboardId(null);
      }
    },
    [dispatch, organization?.id, program?.id]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Leaderboard",
        tooltip: "Name of the leaderboard",
        accessor: "name",
        Cell: ({ value }) => (
          <div className="manager-program-settings__cell-stack">
            <strong>{value || "Untitled leaderboard"}</strong>
          </div>
        ),
      },
      {
        Header: "Type",
        tooltip: "How leaderboard ranking is calculated",
        accessor: "leaderboard_type.name",
        Cell: ({ value }) => (
          <div className="manager-program-settings__cell-stack">
            <span>{getLeaderboardTypeLabel(value)}</span>
          </div>
        ),
      },
    ],
    []
  );

  const { getTableProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data: leaderboards,
  });

  const leaderboardCountLabel = `${leaderboards.length.toLocaleString()} leaderboard${
    leaderboards.length === 1 ? "" : "s"
  }`;

  return (
    <>
      <div className="manager-program-settings__table-meta">
        <span className="manager-program-settings__table-count">
          {loading ? t("loading") : leaderboardCountLabel}
        </span>

        {!loading && !errorMessage && leaderboards.length > 0 && (
          <span className="manager-program-settings__table-hint">
            Edit leaderboard settings or remove unused leaderboard definitions.
          </span>
        )}
      </div>

      {loading && (
        <div className="manager-program-settings__empty">Loading leaderboards...</div>
      )}

      {!loading && errorMessage && (
        <div className="manager-program-settings__empty">{errorMessage}</div>
      )}

      {!loading && !errorMessage && leaderboards.length === 0 && (
        <div className="manager-program-settings__empty">
          No leaderboards are configured for this program.
        </div>
      )}

      {!loading && !errorMessage && leaderboards.length > 0 && (
        <div className="manager-program-settings__table-shell">
          <Table borderless className="manager-program-settings__table table--stack-mobile" {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()}>
                      <span data-tip={column.tooltip} style={{ display: "inline-block" }}>
                        {column.render("Header")}
                      </span>
                    </th>
                  ))}
                  <CanEdit>
                    <th>Actions</th>
                  </CanEdit>
                </tr>
              ))}
            </thead>

            <tbody>
              {rows.map((row) => {
                prepareRow(row);
                const isBusy = busyLeaderboardId === row.original.id;

                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} data-label={typeof cell.column.Header === 'string' ? cell.column.Header : ''}>{cell.render("Cell")}</td>
                    ))}

                    <CanEdit>
                      <td data-label="Actions">
                        <div className="manager-program-settings__actions">
                          <button
                            type="button"
                            data-tip="Edit this leaderboard's settings"
                            className="manager-program-settings__action-btn"
                            onClick={() => onClickEditLeaderboard(row.original.id)}
                            disabled={isBusy}
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            data-tip="Permanently remove this leaderboard"
                            className="manager-program-settings__action-btn is-danger"
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Are you sure you want to delete this leaderboard?"
                                )
                              ) {
                                onDeleteLeaderboard(row.original.id);
                              }
                            }}
                            disabled={isBusy}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </CanEdit>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      <ModalWrapper
        name={modalName}
        isOpen={isOpen}
        setOpen={setOpen}
        toggle={toggle}
        leaderboard={leaderboard}
        setLeaderboard={setLeaderboard}
        setTrigger={setTrigger}
      />
    </>
  );
};

export default Leaderboards;
